import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { loadStripe } from '@stripe/stripe-js';
import { XMarkIcon } from '@heroicons/react/24/solid';
import {
  BankHolidays,
  Order,
  TimeslotResponse,
  TimeSlotValue,
} from '../../models/APIModels';
import { axiosKK } from '../../services/networkRequest';
import { isWithinWorkingDays } from '../../utilities/CollectionDateConstraints';
import Button from '../Button';

type ModalState = 'idle' | 'submitting' | 'confirming_3ds' | 'success' | 'error';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY!!);

interface TimeSlotModalProps {
  show: boolean;
  close: () => void;
  orders: Order[];
  onSuccess: (bookedOrderId: number) => void;
  onNoPaymentMethod: () => void;
}

export function TimeSlotModal({
  show,
  close,
  orders,
  onSuccess,
  onNoPaymentMethod,
}: TimeSlotModalProps): JSX.Element | null {
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlotValue | null>(null);
  const [modalState, setModalState] = useState<ModalState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [bankHolidays, setBankHolidays] = useState<BankHolidays | null>(null);
  const [serverDate, setServerDate] = useState<Date | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    fetch('https://www.gov.uk/bank-holidays.json')
      .then((response) => response.json())
      .then((data) => {
        if (!cancelled) setBankHolidays(data);
      })
      .catch(console.log);

    axiosKK
      .get('time')
      .then((response) => {
        return response && response.data && response.data.time
          ? new Date(response.data.time)
          : new Date();
      })
      .catch(() => new Date())
      .then((date) => {
        if (!cancelled) setServerDate(date);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const eligibleOrders = useMemo(
    () => {
      const now = serverDate || new Date();
      return orders.filter((o) => {
        if (!o.collectionDate) return false;
        const address = { country: (o.collectionCountry as string) || undefined };
        return !isWithinWorkingDays(o.collectionDate, now, 3, bankHolidays, address);
      });
    },
    [orders, bankHolidays, serverDate],
  );

  useEffect(() => {
    if (show) {
      setSelectedSlot(null);
      setModalState('idle');
      setErrorMessage('');
      setSelectedOrderId(eligibleOrders.length === 1 ? eligibleOrders[0].id : null);
    }
  }, [show, eligibleOrders]);

  if (!show) return null;

  const handleSubmit = async () => {
    if (!selectedOrderId || !selectedSlot) return;

    setModalState('submitting');
    setErrorMessage('');

    try {
      const { data } = await axiosKK.post<TimeslotResponse>(
        '/order/timeslot',
        { orderId: selectedOrderId, timeSlot: selectedSlot },
      );

      if (data.paymentIntentClientSecret) {
        if (mountedRef.current) setModalState('confirming_3ds');
        const stripe = await stripePromise;
        if (!stripe) {
          throw new Error('Payment gateway not available');
        }

        const { error } = await stripe.confirmCardPayment(
          data.paymentIntentClientSecret,
        );

        if (error) {
          if (mountedRef.current) {
            setModalState('error');
            setErrorMessage(error.message || 'Card authentication failed.');
          }
          return;
        }
      }

      if (!mountedRef.current) return;
      setModalState('success');
      setTimeout(() => {
        if (!mountedRef.current) return;
        onSuccess(selectedOrderId);
        close();
      }, 2000);
    } catch (err: any) {
      const status = err?.response?.status;
      const responseData = err?.response?.data;

      if (status === 400 && responseData?.message?.includes('No payment method')) {
        if (mountedRef.current) onNoPaymentMethod();
        return;
      }

      if (!mountedRef.current) return;

      if (status === 409) {
        setModalState('error');
        setErrorMessage(
          'A time-slot has already been booked for this order.',
        );
        return;
      }

      setModalState('error');
      setErrorMessage(
        responseData?.message || 'Something went wrong. Please try again.',
      );
    }
  };

  const canSubmit =
    selectedOrderId !== null &&
    selectedSlot !== null &&
    modalState === 'idle';

  return ReactDOM.createPortal(
    <div className="modern-modal__overlay" onClick={close}>
      <div
        className="modern-modal__container timeslot-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modern-modal__header">
          <h2>Book Your Collection Time-Slot</h2>
          <Button
            variant="tertiary"
            size="sm"
            iconOnly
            className="modern-modal__close"
            aria-label="Close"
            onClick={close}
          >
            <XMarkIcon aria-hidden="true" />
          </Button>
        </div>

        {modalState === 'success' ? (
          <div className="timeslot-modal__success">
            <span className="timeslot-modal__success-icon">&#10003;</span>
            <h3>Time-slot booked successfully!</h3>
            <p>Your collection time-slot has been confirmed.</p>
          </div>
        ) : eligibleOrders.length === 0 ? (
          <div className="timeslot-modal__body">
            <p className="timeslot-modal__info-text">
              We need at least <strong>3 working days</strong> notice to book a
              collection time-slot. None of your current orders have a
              collection date far enough in advance.
            </p>
            <p className="timeslot-modal__notice timeslot-modal__notice--small">
              Please contact us if you need to reschedule your collection.
            </p>
          </div>
        ) : (
          <>
            <div className="timeslot-modal__body">
              <p className="timeslot-modal__info-text">
                Request your (optional) Pre or Post 12 collection for{' '}
                <strong>&pound;35</strong>
              </p>

              {eligibleOrders.length > 1 && (
                <div className="timeslot-modal__order-select">
                  <label htmlFor="timeslot-order">Select Order</label>
                  <select
                    id="timeslot-order"
                    value={selectedOrderId ?? ''}
                    onChange={(e) =>
                      setSelectedOrderId(
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                  >
                    <option value="">-- Choose an order --</option>
                    {eligibleOrders.map((order) => (
                      <option key={order.id} value={order.id}>
                        Order #{order.id}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {eligibleOrders.length === 1 && (
                <p className="timeslot-modal__order-label">
                  Order <strong>#{eligibleOrders[0].id}</strong>
                </p>
              )}

              <div className="timeslot-modal__slot-options">
                <label
                  className={`timeslot-modal__slot-option${
                    selectedSlot === 'pre_12'
                      ? ' timeslot-modal__slot-option--selected'
                      : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="timeslot"
                    value="pre_12"
                    checked={selectedSlot === 'pre_12'}
                    onChange={() => setSelectedSlot('pre_12')}
                  />
                  <span className="timeslot-modal__slot-label">
                    Pre 12 <span className="timeslot-modal__slot-price">(&pound;35)</span>
                  </span>
                </label>
                <label
                  className={`timeslot-modal__slot-option${
                    selectedSlot === 'post_12'
                      ? ' timeslot-modal__slot-option--selected'
                      : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="timeslot"
                    value="post_12"
                    checked={selectedSlot === 'post_12'}
                    onChange={() => setSelectedSlot('post_12')}
                  />
                  <span className="timeslot-modal__slot-label">
                    Post 12 <span className="timeslot-modal__slot-price">(&pound;35)</span>
                  </span>
                </label>
              </div>

              <p className="timeslot-modal__notice">
                We need to know <strong>3 working days</strong> in advance in
                order to book a time slot for collection.
              </p>
              <p className="timeslot-modal__notice timeslot-modal__notice--small">
                *Please note prices may vary for shipping orders.
              </p>

              {modalState === 'error' && errorMessage && (
                <div className="timeslot-modal__error">{errorMessage}</div>
              )}

              {modalState === 'confirming_3ds' && (
                <div className="timeslot-modal__info-text">
                  Completing card authentication...
                </div>
              )}
            </div>

            <div className="modern-modal__footer timeslot-modal__footer">
              <button
                className="modern-modal__cta"
                type="button"
                disabled={!canSubmit}
                onClick={handleSubmit}
              >
                {modalState === 'submitting' || modalState === 'confirming_3ds'
                  ? 'Processing...'
                  : 'Submit Time-Slot'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
